"use client";

import type { ReactElement, ReactNode } from "react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import Icon, { type IconName } from "./Icon";

const toUrlSafeId = (str: string) => {
	return str
		.toLowerCase()
		.replace(/[^a-z0-9\s]/g, "")
		.trim()
		.replace(/\s+/g, "-");
};

const getCurrentUrl = () => {
	return `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
};

const extractTextContent = (node: ReactNode): string => {
	if (typeof node === "string") {
		return node;
	}
	if (typeof node === "number") {
		return node.toString();
	}
	if (Array.isArray(node)) {
		return node.map(extractTextContent).join(" ");
	}
	if (node && typeof node === "object" && "props" in node) {
		const element = node as ReactElement<{ children?: ReactNode }>;
		return extractTextContent(element.props.children ?? "");
	}
	return "";
};

type Props = {
	children: ReactNode;
	as?:
		| "h1"
		| "h2"
		| "h3"
		| "h4"
		| "h5"
		| "h6"
		| "p"
		| "span"
		| "a"
		| "div"
		| "label";
	className?: string;
	icon?: IconName;
	iconSize?: number;
	iconClassName?: string;
	copyable?: boolean;
	link?: string;
};

export const AnchoredText = ({
	children,
	link,
	as,
	className,
	icon = "Link",
	iconClassName,
	iconSize = 16,
	copyable = true,
	...rest
}: Props) => {
	const [isCopied, setIsCopied] = useState(false);

	const Component = as ?? "h1";
	const textContent = extractTextContent(children);
	const id = link ?? toUrlSafeId(textContent);

	const handleClick = () => {
		const currentUrl = getCurrentUrl();
		const href = `${currentUrl}#${id}`;

		if (copyable) {
			navigator.clipboard.writeText(href);
			setIsCopied(true);
			setTimeout(() => setIsCopied(false), 1000);
		}
		window.location.href = href;
	};

	return (
		<Component
			id={id}
			className={cn(
				"group relative w-fit no-underline transition-all duration-200 hover:underline hover:underline-offset-4",
				{
					"cursor-pointer": copyable,
				},
				className,
			)}
			onClick={handleClick}
			{...rest}
		>
			{children}
			<Icon
				name={isCopied ? "Check" : icon}
				size={iconSize}
				className={cn(
					"absolute top-1/2 -right-6 translate-x-2 -translate-y-1/2 opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100",
					{
						"text-green-700": isCopied,
						hidden: !copyable,
					},
					iconClassName,
				)}
			/>
		</Component>
	);
};
